import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useParams, useSearchParams } from 'react-router-dom';
import { Col, Row, Space, Spin, Typography } from 'antd';
import type { RangeValue } from 'rc-picker/lib/interface';
import RangePicker from 'components/new-ui/base/date-picker/RangePicker';
import Timeline from 'components/new-ui/base/timeline/Timeline';
import { H2, H3 } from 'components/new-ui/base/typography/styles';
import Button from 'components/new-ui/base/button/Button';
import { getInvoiceHistory } from 'store/invoices/client';
import { GetInvoicesHistoryParams, InvoiceHistoryRecord } from 'store/invoices/types';
import { getHistoryMetadataBasedOnRecord } from 'views/invoices/history-utils';
import UserAvatar from 'components/new-ui/base/avatar/UserAvatar';
import Notification from 'components/ui/base/notification/Notification';
import useInvoices from 'hooks/useInvoices';
import { getCurrencyData } from 'utils/common';
import { Events } from 'utils/events.enum';

const PAGE_SIZE = 20;

const HistoryTab = () => {
    const { invoiceId } = useParams();
    const { invoice } = useInvoices(invoiceId);
    const [searchParams, setSearchParams] = useSearchParams();
    const [history, setHistory] = useState<InvoiceHistoryRecord[]>([]);
    const [datesRange, setDatesRange] = useState<RangeValue<moment.Moment> | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);

    const controllerRef = useRef<AbortController | null>(null);

    const { currencySymbol } = getCurrencyData();

    const fetchPage = useCallback(
        async (pageToLoad: number) => {
            if (!invoiceId) {
                return;
            }

            controllerRef.current?.abort();
            const controller = new AbortController();
            controllerRef.current = controller;

            const isFirstPage = pageToLoad === 0;

            if (isFirstPage) {
                setHasMore(false);
                setPage(0);
                setIsInitialLoading(true);
            } else {
                setIsLoading(true);
            }

            try {
                const startDate = searchParams.get('startDate') ?? undefined;
                const endDate = searchParams.get('endDate') ?? undefined;

                const params: GetInvoicesHistoryParams = {
                    page: pageToLoad,
                    size: PAGE_SIZE,
                    startDate,
                    endDate,
                };

                const { list, pageNumber, pageSize, total } = await getInvoiceHistory(invoiceId, params);

                if (controller.signal.aborted) return;

                const effectiveSize = pageSize || PAGE_SIZE;
                const totalPages = Math.ceil((total ?? 0) / effectiveSize);

                if (isFirstPage) {
                    setHistory(list ?? []);
                } else {
                    setHistory((prev) => {
                        const seen = new Set(prev.map((i) => i.id));
                        const merged = [...prev];
                        for (const item of list ?? []) {
                            if (!seen.has(item.id)) merged.push(item);
                        }
                        return merged;
                    });
                }
                setHasMore(pageNumber + 1 < totalPages && list.length > 0);
                setPage(pageNumber);
            } catch (error) {
                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Error',
                    description: 'Could not get history for this invoice',
                });
            } finally {
                if (!controller.signal.aborted) {
                    if (isFirstPage) {
                        setIsInitialLoading(false);
                    } else {
                        setIsLoading(false);
                    }
                }
            }
        },
        [invoiceId, searchParams]
    );

    const handleDateChange = (range: RangeValue<moment.Moment>) => {
        setDatesRange(range);

        const params: Record<string, string> = {};
        if (range?.[0]) params.startDate = range[0].toISOString();
        if (range?.[1]) params.endDate = range[1].toISOString();

        setSearchParams(params, { replace: true });

        fetchPage(0);
    };

    useEffect(() => {
        const startDateParam = searchParams.get('startDate');
        const endDateParam = searchParams.get('endDate');

        const start = startDateParam ? moment(startDateParam) : undefined;
        const end = endDateParam ? moment(endDateParam) : undefined;

        setDatesRange(start && end ? [start, end] : undefined);

        if (invoiceId) {
            fetchPage(0);
        }

        return () => {
            controllerRef.current?.abort();
        };
    }, [invoiceId, searchParams, fetchPage]);

    // Refresh the history when an invoice action is performed
    useEffect(() => {
        const subscriberId = window.EventBus.subscribe(Events.INVOICE_HISTORY_UPDATED, () => fetchPage(0));

        return () => {
            window.EventBus.unsubscribe(subscriberId);
        };
    }, [fetchPage]);

    const formattedHistory: {
        title: string;
        content: ReactNode;
        id: string;
        createdAt: string;
        createdBy: string | undefined;
        userImageUrl: string | undefined;
    }[] =
        history?.map((el) => {
            const { title, description } = getHistoryMetadataBasedOnRecord(el, currencySymbol, invoice);

            return {
                id: el.id,
                content: description,
                title: title,
                createdAt: el.createdAt,
                createdBy: el?.creatorInfo?.fullName.split(' ')[0],
                userImageUrl: el?.creatorInfo?.profileLink,
            };
        }) || [];

    return (
        <Space style={{ width: '100%' }} direction="vertical" size={32}>
            <Row justify="space-between" align="middle">
                <H2>Changelog</H2>
                <div>
                    <RangePicker value={datesRange} onChange={handleDateChange} />
                </div>
            </Row>

            <Spin spinning={isInitialLoading}>
                <div style={{ minHeight: formattedHistory.length ? undefined : 120 }}>
                    <Timeline>
                        {formattedHistory?.map(({ id, title, content, createdAt, createdBy, userImageUrl }) => {
                            const formattedDate = createdAt && moment(createdAt).format('DD/MM/YYYY, hh:mm:ss A');
                            const creatorName = createdBy || 'System';

                            return (
                                <Timeline.Item key={id}>
                                    <Row gutter={24} justify="space-between">
                                        <Col
                                            lg={16}
                                            md={24}
                                            style={{
                                                maxWidth: '75ch',
                                                marginBottom: 16,
                                            }}
                                        >
                                            <H3>{title}</H3>
                                            <Typography.Text>{content}</Typography.Text>
                                        </Col>

                                        <Col lg={8} xl={6} md={24}>
                                            <Row gutter={8} align="middle" justify="space-between">
                                                <Col>
                                                    <UserAvatar
                                                        size="small"
                                                        style={{ marginRight: 4 }}
                                                        src={userImageUrl}
                                                        userName={creatorName}
                                                    />
                                                    <span>{creatorName}</span>
                                                </Col>
                                                <Col>{formattedDate}</Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>
                </div>
            </Spin>

            {hasMore && formattedHistory?.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                    <Button
                        loading={isLoading}
                        onClick={() => {
                            const nextPage = page + 1;
                            fetchPage(nextPage);
                        }}
                    >
                        Show more
                    </Button>
                </div>
            )}
        </Space>
    );
};

export default HistoryTab;
